import { useState, useEffect } from "react";
import { 
  Monitor, Smartphone, Tablet, Globe, Trash2, LogOut, 
  Shield, Clock, MapPin, RefreshCw, AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect device type from user agent
function getDeviceIcon(userAgent) {
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
    return Smartphone;
  }
  if (ua.includes('tablet') || ua.includes('ipad')) {
    return Tablet;
  }
  return Monitor;
}

// Parse user agent for display
function parseUserAgent(userAgent) {
  const ua = userAgent.toLowerCase();
  
  // Detect browser
  let browser = "Browser sconosciuto";
  if (ua.includes('chrome') && !ua.includes('edg')) browser = "Chrome";
  else if (ua.includes('firefox')) browser = "Firefox";
  else if (ua.includes('safari') && !ua.includes('chrome')) browser = "Safari";
  else if (ua.includes('edg')) browser = "Edge";
  else if (ua.includes('opera')) browser = "Opera";
  
  // Detect OS
  let os = "Sistema sconosciuto";
  if (ua.includes('windows')) os = "Windows";
  else if (ua.includes('mac os') || ua.includes('macos')) os = "macOS";
  else if (ua.includes('linux')) os = "Linux";
  else if (ua.includes('android')) os = "Android";
  else if (ua.includes('iphone') || ua.includes('ipad')) os = "iOS";
  
  return { browser, os };
}

// Format date relative to now
function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Adesso";
  if (diffMins < 60) return `${diffMins} min fa`;
  if (diffHours < 24) return `${diffHours} ore fa`;
  if (diffDays === 1) return "Ieri";
  if (diffDays < 7) return `${diffDays} giorni fa`;
  
  return date.toLocaleDateString('it-IT', { 
    day: 'numeric', 
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

export default function SessionsManager({ isOpen, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxSessions, setMaxSessions] = useState(10);
  const [sessionToRevoke, setSessionToRevoke] = useState(null);
  const [revokeAllDialogOpen, setRevokeAllDialogOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/auth/sessions`);
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
        setMaxSessions(data.max_sessions || 10);
      } else {
        toast.error("Errore nel caricamento delle sessioni");
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevoking(true);
    try {
      const response = await authFetch(`${API}/auth/sessions/${sessionId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Sessione disconnessa");
        setSessions(sessions.filter(s => s.session_id !== sessionId));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella disconnessione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setRevoking(false);
      setSessionToRevoke(null);
    }
  };

  const handleRevokeAllOther = async () => {
    setRevoking(true);
    try {
      const response = await authFetch(`${API}/auth/sessions`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Altre sessioni disconnesse");
        // Keep only current session
        setSessions(sessions.filter(s => s.is_current));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella disconnessione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setRevoking(false);
      setRevokeAllDialogOpen(false);
    }
  };

  const otherSessionsCount = sessions.filter(s => !s.is_current).length;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Sessioni Attive
            </DialogTitle>
            <DialogDescription>
              Gestisci i dispositivi connessi al tuo account. Puoi disconnettere le sessioni che non riconosci.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">
                <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nessuna sessione attiva</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Current session indicator */}
                <div className="flex items-center justify-between text-sm text-zinc-400 px-1">
                  <span>{sessions.length} di {maxSessions} sessioni attive</span>
                  {otherSessionsCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => setRevokeAllDialogOpen(true)}
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      Disconnetti altre ({otherSessionsCount})
                    </Button>
                  )}
                </div>

                {/* Sessions list */}
                {sessions.map((session) => {
                  const DeviceIcon = getDeviceIcon(session.user_agent);
                  const { browser, os } = parseUserAgent(session.user_agent);
                  
                  return (
                    <div
                      key={session.session_id}
                      className={`p-4 rounded-lg border transition-colors ${
                        session.is_current 
                          ? 'bg-primary/10 border-primary/30' 
                          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            session.is_current ? 'bg-primary/20' : 'bg-zinc-800'
                          }`}>
                            <DeviceIcon className={`w-5 h-5 ${
                              session.is_current ? 'text-primary' : 'text-zinc-400'
                            }`} />
                          </div>
                          
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">
                                {browser} su {os}
                              </span>
                              {session.is_current && (
                                <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded-full">
                                  Sessione corrente
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-3 mt-1 text-sm text-zinc-400">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {session.ip_address}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatRelativeTime(session.last_seen_at)}
                              </span>
                            </div>
                            
                            <p className="text-xs text-zinc-500 mt-1 truncate max-w-md">
                              ID: {session.device_id}
                            </p>
                          </div>
                        </div>

                        {!session.is_current && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => setSessionToRevoke(session)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={onClose}>
              Chiudi
            </Button>
            <Button onClick={fetchSessions} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm revoke single session */}
      <AlertDialog open={!!sessionToRevoke} onOpenChange={() => setSessionToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Disconnetti sessione?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Questo dispositivo verrà disconnesso e dovrà effettuare nuovamente il login per accedere.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => sessionToRevoke && handleRevokeSession(sessionToRevoke.session_id)}
              disabled={revoking}
              className="bg-red-600 hover:bg-red-700"
            >
              {revoking ? "Disconnessione..." : "Disconnetti"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm revoke all other sessions */}
      <AlertDialog open={revokeAllDialogOpen} onOpenChange={setRevokeAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Disconnetti tutti gli altri dispositivi?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tutti i dispositivi tranne quello corrente verranno disconnessi. 
              Dovranno effettuare nuovamente il login per accedere.
              <br /><br />
              <strong>{otherSessionsCount} sessioni</strong> verranno disconnesse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeAllOther}
              disabled={revoking}
              className="bg-red-600 hover:bg-red-700"
            >
              {revoking ? "Disconnessione..." : "Disconnetti tutti"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
